import type { AutomationActionResult, AutomationRuleDto, AutomationRunDto } from '@fos/shared/contracts/automations';
import { incidentPath } from '@fos/shared/contracts/monitoring';
import {
  AUTOMATION_ACTION_LABELS,
  AUTOMATION_ACTION_STATE_LABELS,
  AUTOMATION_RUN_STATUS_LABELS,
  AUTOMATION_RUN_STATUS_TONES,
  automationActionsText,
  automationTriggerText,
} from '@fos/shared/labels';
import { Bot, Pencil, Play, Power, ShieldCheck, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatDateTimeShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { AIBlock } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Sheet } from '../../../ui/Dialog';
import { ErrorState, ListSkeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';

/**
 * Karta reguły (panel boczny): wyzwalacz i akcje, zgoda na „Zleć Claude” (owner/admin, potwierdzenie hasłem —
 * okno step-up), włącz / wyłącz, „Wyślij teraz” (harmonogram), usunięcie (archiwum) i historia uruchomień z wynikiem
 * każdej akcji (błędy i powody pominięcia po polsku).
 */
export function RuleSheet({
  rule,
  open,
  onOpenChange,
  onEdit,
}: {
  rule: AutomationRuleDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (rule: AutomationRuleDto) => void;
}) {
  const utils = trpc.useUtils();
  const runs = trpc.automations.runs.useQuery(
    { ruleId: rule?.id ?? '', limit: 20 },
    { enabled: open && Boolean(rule), refetchInterval: open ? 15_000 : false },
  );
  const setEnabled = trpc.automations.setEnabled.useMutation();
  const consent = trpc.automations.setClaudeConsent.useMutation();
  const archive = trpc.automations.archive.useMutation();
  const runNow = trpc.automations.runNow.useMutation();
  if (!rule) return null;

  const act = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast.success(done);
      await utils.automations.invalidate();
    } catch (err) {
      if (appErrorCode(err) === 'STEP_UP_REQUIRED') return;
      toast.error(errorMessage(err));
    }
  };
  const busy = setEnabled.isPending || consent.isPending || archive.isPending || runNow.isPending;
  // wyłączyć może też owner/admin (cudza reguła), włącza tylko autor
  const canToggle = !rule.archivedAt && (rule.isMine || rule.enabled);

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={rule.name}
      description={rule.isMine ? undefined : `Reguła: ${rule.owner.name}`}
    >
      <div className="flex flex-col gap-5 px-5 pb-6">
        <dl className="flex flex-col gap-2 text-body-s">
          <div>
            <dt className="text-caption text-fg-muted">Kiedy</dt>
            <dd className="text-fg">{automationTriggerText(rule.trigger)}</dd>
          </div>
          <div>
            <dt className="text-caption text-fg-muted">Co zrobić</dt>
            <dd className="text-fg">{automationActionsText(rule.actions)}</dd>
          </div>
          <div>
            <dt className="text-caption text-fg-muted">Stan</dt>
            <dd>
              <Status tone={rule.enabled ? 'success' : 'neutral'}>{rule.enabled ? 'Włączona' : 'Wyłączona'}</Status>
            </dd>
          </div>
        </dl>

        {rule.claude.required && (
          <AIBlock
            tone="subtle"
            title="Zlecanie pracy Claude"
            actions={
              rule.canConsent ? (
                rule.claude.allowed ? (
                  <Button
                    size="s"
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      act(() => consent.mutateAsync({ id: rule.id, allowed: false }), 'Zgoda na Claude cofnięta')
                    }
                  >
                    Cofnij zgodę
                  </Button>
                ) : (
                  <Button
                    size="s"
                    variant="secondary"
                    icon={ShieldCheck}
                    disabled={busy}
                    onClick={() =>
                      act(
                        () => consent.mutateAsync({ id: rule.id, allowed: true }),
                        'Ta reguła może zlecać pracę Claude',
                      )
                    }
                  >
                    Zezwól (potwierdź hasłem)
                  </Button>
                )
              ) : undefined
            }
          >
            {rule.claude.allowed ? (
              <p>
                Ta reguła może zlecać pracę Claude — zgoda: {rule.claude.allowedBy ?? '—'}
                {rule.claude.allowedAt ? `, ${formatDateTimeShort(rule.claude.allowedAt)}` : ''}. Limit zleceń na dobę
                obowiązuje zawsze.
              </p>
            ) : rule.claude.stale ? (
              <p>Reguła zmieniła się po udzieleniu zgody — zlecenia dla Claude są wstrzymane do nowej zgody.</p>
            ) : (
              <p>Bez zgody właściciela albo administratora akcja „Zleć Claude” jest pomijana (reszta reguły działa).</p>
            )}
          </AIBlock>
        )}

        <div className="flex flex-wrap gap-1.5">
          {rule.isMine && !rule.archivedAt && (
            <Button size="s" variant="secondary" icon={Pencil} onClick={() => onEdit(rule)}>
              Edytuj
            </Button>
          )}
          {canToggle && (
            <Button
              size="s"
              variant="secondary"
              icon={Power}
              disabled={busy}
              onClick={() =>
                act(
                  () => setEnabled.mutateAsync({ id: rule.id, enabled: !rule.enabled }),
                  rule.enabled ? 'Reguła wyłączona' : 'Reguła włączona',
                )
              }
            >
              {rule.enabled ? 'Wyłącz' : 'Włącz'}
            </Button>
          )}
          {rule.isMine && rule.enabled && rule.trigger.type === 'schedule.daily' && (
            <Button
              size="s"
              variant="secondary"
              icon={Play}
              disabled={busy}
              onClick={() => act(() => runNow.mutateAsync({ id: rule.id }), 'Uruchomiono — wynik za chwilę w historii')}
            >
              Wyślij teraz
            </Button>
          )}
          {!rule.archivedAt && (
            <Button
              size="s"
              variant="ghost"
              icon={Trash2}
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await archive.mutateAsync({ id: rule.id });
                  onOpenChange(false);
                }, 'Reguła usunięta (historia zostaje)')
              }
            >
              Usuń
            </Button>
          )}
        </div>

        <section aria-label="Historia uruchomień">
          <h3 className="text-caption text-fg-muted">Historia uruchomień</h3>
          {runs.isPending ? (
            <ListSkeleton rows={3} />
          ) : runs.isError ? (
            <ErrorState compact onRetry={() => void runs.refetch()} retrying={runs.isFetching} />
          ) : runs.data.length === 0 ? (
            <p className="mt-2 text-caption text-fg-muted">Jeszcze nie było uruchomień.</p>
          ) : (
            <ul className="mt-1">
              {runs.data.map((run) => (
                <RunItem key={run.id} run={run} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </Sheet>
  );
}

function RunItem({ run }: { run: AutomationRunDto }) {
  const incident = run.trigger.incidentId ? incidentPath(run.trigger.incidentId) : null;
  return (
    <li className="border-b border-line py-2.5">
      <div className="flex items-center gap-2">
        <Status tone={AUTOMATION_RUN_STATUS_TONES[run.status]}>{AUTOMATION_RUN_STATUS_LABELS[run.status]}</Status>
        <span className="ml-auto shrink-0 text-caption text-fg-muted">{formatDateTimeShort(run.createdAt)}</span>
      </div>
      <p className="mt-1 text-body-s break-words text-fg">
        {incident ? (
          <Link to={incident} className="hover:underline focus-visible:outline-2 focus-visible:outline-accent">
            {run.trigger.label}
          </Link>
        ) : (
          run.trigger.label
        )}
      </p>
      {run.error && run.status === 'skipped' && <p className="text-caption text-fg-muted">{run.error}</p>}
      {run.actions.length > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5">
          {run.actions.map((a, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: kolejność akcji jest stała w uruchomieniu
            <ActionLine key={i} action={a} />
          ))}
        </ul>
      )}
    </li>
  );
}

function ActionLine({ action }: { action: AutomationActionResult }) {
  const label = action.step === 'email' ? 'Mail na mój adres' : AUTOMATION_ACTION_LABELS[action.type];
  const tone =
    action.state === 'done'
      ? 'text-fg-secondary'
      : action.state === 'failed' || action.state === 'uncertain'
        ? 'text-danger'
        : 'text-fg-muted';
  const link =
    action.ref?.type === 'task'
      ? `/zadania/${action.ref.id}`
      : action.ref?.type === 'agent_run'
        ? `/agenci/${action.ref.id}`
        : action.ref?.type === 'event'
          ? `/inbox?zdarzenie=${action.ref.id}`
          : null;
  return (
    <li className={`flex items-start gap-1.5 text-caption ${tone}`}>
      {action.type === 'delegate_claude' && (
        <Bot size={13} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
      )}
      <span className="min-w-0 break-words">
        {label}: {AUTOMATION_ACTION_STATE_LABELS[action.state]}
        {action.message ? ` — ${action.message}` : ''}
        {action.text ? ` — ${action.text}` : ''}
        {action.simulated ? ' (symulacja)' : ''}
        {link && (
          <>
            {' · '}
            <Link
              to={link}
              className="text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              Otwórz
            </Link>
          </>
        )}
      </span>
    </li>
  );
}
